// src/contentful/mappers.js
// Data transformation functions for Contentful responses
// Updated to match actual Contentful schema

/**
 * Format a date string into a human-readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Map a Contentful article entry to normalized article object
 * @param {object} article - Raw Contentful article entry
 * @returns {object} Normalized article
 *
 * Schema notes:
 * - NO isFeatured field on Article
 * - NO tagsCollection
 * - NO relatedArticlesCollection
 */
export const mapArticle = (article) => {
  if (!article) return null;

  // Determine if this is "News" based on category
  const isNews = article.category?.toLowerCase() === 'news';

  return {
    id: article.sys?.id,
    type: isNews ? 'news' : 'article',
    slug: article.slug,
    title: article.title,
    summary: article.heroIntro,
    category: article.category,
    publishedAt: article.publishedDate,
    publishedFormatted: formatDate(article.publishedDate),
    readingTime: article.readingTime,
    isFeatured: article.isFeatured || false,
    heroImage: article.heroImage
      ? {
          url: article.heroImage.url,
          alt: article.heroImage.description || article.title,
          width: article.heroImage.width,
          height: article.heroImage.height,
        }
      : null,
    author: article.author
      ? {
          id: article.author.sys?.id,
          name: article.author.name,
          role: article.author.role,
          avatarUrl: article.author.avatar?.url,
          linkedIn: article.author.linkedInUrl,
        }
      : null,
    topics: (article.topicsCollection?.items ?? []).map(t => ({
      id: t?.sys?.id,
      name: t?.name,
      slug: t?.slug,
      tags: t?.tags ?? [],
      description: t?.description ?? '',
    })).filter(t => t.name),
    tags: [], // No tagsCollection in schema
    body: article.body, // Rich text { json, links } - use body.json for rendering
    relatedArticles: [], // No relatedArticlesCollection in schema
  };
};

/**
 * Map an article to a preview format (for cards)
 * @param {object} article - Raw Contentful article entry
 * @returns {object} Article preview
 */
export const mapArticlePreview = (article) => {
  if (!article) return null;

  const isNews = article.category?.toLowerCase() === 'news';

  return {
    id: article.sys?.id,
    type: isNews ? 'news' : 'article',
    slug: article.slug,
    title: article.title,
    summary: article.heroIntro,
    category: article.category,
    publishedAt: article.publishedDate,
    publishedFormatted: formatDate(article.publishedDate),
    readingTime: article.readingTime,
    heroImage: article.heroImage
      ? {
          url: article.heroImage.url,
          alt: article.heroImage.description || article.title,
        }
      : null,
  };
};

/**
 * Map a Contentful case study entry to normalized case study object
 * @param {object} caseStudy - Raw Contentful case study entry
 * @returns {object} Normalized case study
 *
 * Schema notes:
 * - NO region, NO heroImage
 * - NO challenge/approach/results rich text fields
 * - NO metricsCollection - metrics is JSON scalar
 * - NO technologiesCollection, NO testimonial
 * - NO relatedCaseStudiesCollection
 */
export const mapCaseStudy = (caseStudy) => {
  if (!caseStudy) return null;

  // Handle metrics as JSON scalar (could be object, array, or null)
  let metricsArray = [];
  if (caseStudy.metrics) {
    // If it's already an array, use it directly
    if (Array.isArray(caseStudy.metrics)) {
      metricsArray = caseStudy.metrics;
    }
    // If it's an object with key-value pairs, convert to array
    else if (typeof caseStudy.metrics === 'object') {
      metricsArray = Object.entries(caseStudy.metrics).map(([label, value]) => ({
        label,
        value: String(value),
      }));
    }
  }

  return {
    id: caseStudy.sys?.id,
    type: 'caseStudy',
    slug: caseStudy.slug,
    title: caseStudy.title,
    summary: caseStudy.heroIntro,
    publishedAt: caseStudy.publishedDate,
    publishedFormatted: formatDate(caseStudy.publishedDate),
    isFeatured: caseStudy.isFeatured || false,
    clientName: caseStudy.clientName,
    industry: caseStudy.industry,
    region: null, // Not in schema
    headlineOutcome: caseStudy.headlineOutcome,
    heroImage: null, // CaseStudy doesn't have heroImage in schema
    metrics: metricsArray,
    topics: (caseStudy.topicsCollection?.items ?? []).map(t => ({
      id: t?.sys?.id,
      name: t?.name,
      slug: t?.slug,
      tags: t?.tags ?? [],
      description: t?.description ?? '',
    })).filter(t => t.name),
    challenge: null, // Not in schema
    approach: null, // Not in schema
    results: null, // Not in schema
    technologies: [], // Not in schema
    testimonial: null, // Not in schema
    relatedCaseStudies: [], // Not in schema
  };
};

/**
 * Map a case study to a preview format (for cards)
 * @param {object} caseStudy - Raw Contentful case study entry
 * @returns {object} Case study preview
 */
export const mapCaseStudyPreview = (caseStudy) => {
  if (!caseStudy) return null;

  return {
    id: caseStudy.sys?.id,
    type: 'caseStudy',
    slug: caseStudy.slug,
    title: caseStudy.title,
    summary: caseStudy.heroIntro,
    clientName: caseStudy.clientName,
    industry: caseStudy.industry,
    headlineOutcome: caseStudy.headlineOutcome,
    heroImage: null, // CaseStudy doesn't have heroImage in schema
  };
};

/**
 * Map the resources list response to a unified array
 * Combines articles and case studies into a single sortable array
 * @param {object} data - Raw Contentful response from RESOURCES_LIST_QUERY
 * @returns {object} { resources: array, totalCount: number }
 */
export const mapResourcesList = (data) => {
  if (!data) return { resources: [], totalCount: 0 };

  const articles = data.articleCollection?.items?.map(mapArticle).filter(Boolean) || [];
  const caseStudies = data.caseStudyCollection?.items?.map(mapCaseStudy).filter(Boolean) || [];

  const resources = [...articles, ...caseStudies];

  // Sort by publishedAt date (newest first)
  resources.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

  return {
    resources,
    totalCount: resources.length,
    articleCount: articles.length,
    caseStudyCount: caseStudies.length,
  };
};

/**
 * Extract all unique topics from a list of resources
 * @param {array} resources - Array of mapped resources
 * @returns {array} Unique topic names
 */
export const extractTopics = (resources) => {
  const topicsMap = new Map();
  resources.forEach((resource) => {
    resource.topics?.forEach((topic) => {
      if (topic?.name && !topicsMap.has(topic.name)) {
        topicsMap.set(topic.name, topic);
      }
    });
  });
  return Array.from(topicsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Extract all unique authors from a list of resources
 * @param {array} resources - Array of mapped resources
 * @returns {array} Unique author objects
 */
export const extractAuthors = (resources) => {
  const authorsMap = new Map();
  resources.forEach((resource) => {
    if (resource.author?.name) {
      authorsMap.set(resource.author.name, resource.author);
    }
  });
  return Array.from(authorsMap.values());
};
